import area from '@turf/area';
import canonical from '../../constants/ccCanonical.json';

export const CC_SCHEMA = canonical.schema_id;
export const CC_SELECTION_KEY = 'cc.dataset.selection.v1';
const DATASETS = {
  2024: { id: 'tr-2024-legacy-v1', layer: 'Fieldops:cc_tr_2024_v1' },
  2026: { id: 'tr-2026-phase5-v1', layer: 'Fieldops:cc_tr_2026_v1' },
};
const fail = () => { throw new Error('Invalid Crop Intelligence contract'); };
const finite = (value) => typeof value === 'number' && Number.isFinite(value);

export function validateMetadata(data) {
  if (!data || data.country_code !== 'TR' || data.class_schema !== CC_SCHEMA ||
      !Array.isArray(data.classes) || data.classes.length !== 11 || !Array.isArray(data.datasets)) fail();
  const classes = {};
  data.classes.forEach((entry) => {
    const expected = canonical.classes.find((item) => item.code === entry.code);
    if (!Number.isInteger(entry.code) || !expected || classes[entry.code] ||
        typeof entry.name !== 'string' || !entry.name.trim() ||
        entry.label_key !== expected.label_key || entry.is_crop !== expected.is_crop ||
        entry.fill?.toUpperCase() !== expected.fill || entry.stroke?.toUpperCase() !== expected.stroke) fail();
    classes[entry.code] = entry;
  });
  const ids = new Set();
  data.datasets.forEach((dataset) => {
    const expected = DATASETS[dataset.year];
    if (!Number.isInteger(dataset.year) || !expected || dataset.id !== expected.id ||
        dataset.layer !== expected.layer || dataset.class_schema !== CC_SCHEMA || ids.has(dataset.id)) fail();
    ids.add(dataset.id);
  });
  if (data.datasets.length ? !data.datasets.some((d) => d.year === data.default_year) : data.default_year !== null) fail();
  // Older metadata omits rendering/fallback fields: use the versioned SLD contract.
  if (data.rendering && (data.rendering.fill_opacity !== canonical.rendering.fill_opacity ||
      data.rendering.stroke_width !== canonical.rendering.stroke_width)) fail();
  if (data.unmapped && Object.keys(canonical.unmapped).some((key) => data.unmapped[key] !== canonical.unmapped[key])) fail();
  return { ...data, classes, rendering: canonical.rendering, unmapped: canonical.unmapped };
}

export function validateStats(data, dataset, country, regionKey) {
  if (!data || data.country_code !== country || data.year !== dataset.year ||
      data.dataset_id !== dataset.id || data.class_schema !== dataset.class_schema ||
      data.region_key !== regionKey || data.area_basis !== 'sum_of_clipped_classified_polygon_areas' ||
      !finite(data.total_area_ha) || data.total_area_ha < 0 || !finite(data.agricultural_area_ha) ||
      data.agricultural_area_ha < 0 || data.agricultural_area_ha > data.total_area_ha || !Array.isArray(data.stats)) fail();
  const seen = new Set();
  data.stats.forEach((row) => {
    if (!Number.isInteger(row.crop) || row.crop < 1 || row.crop > 11 || seen.has(row.crop) ||
        !finite(row.crop_area_ha) || row.crop_area_ha <= 0 || !finite(row.crop_percentage) ||
        row.crop_percentage < 0 || row.crop_percentage > 100 || row.total_area_ha !== data.total_area_ha) fail();
    seen.add(row.crop);
  });
  const total = data.stats.reduce((sum, row) => sum + row.crop_area_ha, 0);
  const agriculture = data.stats.reduce((sum, row) => sum + (canonical.classes.find((entry) => entry.code === row.crop).is_crop ? row.crop_area_ha : 0), 0);
  const close = (left, right) => Math.abs(left - right) <= Math.max(1e-8, Math.abs(right) * 1e-8);
  if (!close(total, data.total_area_ha) || !close(agriculture, data.agricultural_area_ha) ||
      data.stats.some((row) => !close(row.crop_percentage, 100 * row.crop_area_ha / data.total_area_ha))) fail();
  if (!data.stats.length && (data.total_area_ha !== 0 || data.agricultural_area_ha !== 0)) fail();
  return data;
}

export function countryForRegion(region) {
  const p = region?.properties;
  if (!p?.key) return 'TR';
  if (p.country_code) return p.country_code;
  const root = (p.country_key || p.key).split('-')[0].toLowerCase();
  return { tur: 'TR', aze: 'AZ', nld: 'NL', rou: 'RO', svn: 'SI', svk: 'SK', pol: 'PL', cze: 'CZ' }[root] || 'unsupported';
}

export function classForCode(metadata, code) {
  return Number.isInteger(code) && metadata.classes[code] ? metadata.classes[code] : metadata.unmapped;
}

export function classLabel(metadata, code, t) {
  const entry = classForCode(metadata, code);
  return t(entry.label_key, { defaultValue: entry.name });
}

export function featureStyle(metadata, feature) {
  const entry = classForCode(metadata, feature.properties?.crop_code);
  return { color: entry.stroke, fillColor: entry.fill, fillOpacity: metadata.rendering.fill_opacity,
    weight: metadata.rendering.stroke_width };
}

export function polygonAreaHa(feature) {
  const published = feature.properties?.area_ha;
  if ((typeof published === 'number' || (typeof published === 'string' && published.trim())) &&
      Number.isFinite(Number(published)) && Number(published) >= 0) return Number(published);
  try { return area(feature) / 10000; } catch (error) { return null; }
}

export function isPolygonGeometry(geometry) {
  if (!geometry || !['Polygon', 'MultiPolygon'].includes(geometry.type)) return false;
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return Array.isArray(polygons) && polygons.length > 0 && polygons.every((polygon) =>
    Array.isArray(polygon) && polygon.length > 0 && polygon.every((ring) =>
      Array.isArray(ring) && ring.length >= 4 && ring.every((point) => Array.isArray(point) &&
        finite(point[0]) && finite(point[1]) && Math.abs(point[0]) <= 180 && Math.abs(point[1]) <= 90) &&
      ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]));
}

export function investigationFeature(feature, dataset) {
  return { type: 'Feature', id: `${dataset.id}:${feature.properties.fid || feature.id}`,
    properties: { ...feature.properties, asset_type: 'cc_polygon', dataset_id: dataset.id },
    geometry: feature.geometry };
}
